import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createExpenseConcept,
  deleteExpenseConcept,
  listExpenseConcepts,
  type ExpenseConceptItem,
  updateExpenseConcept,
} from "@/lib/api";

type FormState = {
  conceptType: "fixed" | "variable";
  name: string;
  fixedValue: string;
  isBillable: boolean;
  isGross: boolean;
  generatesDebt: boolean;
  status: "active" | "inactive";
};

const emptyForm: FormState = {
  conceptType: "fixed",
  name: "",
  fixedValue: "",
  isBillable: true,
  isGross: false,
  generatesDebt: true,
  status: "active",
};

export default function GastosTab() {
  const { session } = useSession();
  const [items, setItems] = useState<ExpenseConceptItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listExpenseConcepts(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar gastos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function openCreateModal(): void {
    resetForm();
    setShowModal(true);
  }

  function editItem(item: ExpenseConceptItem): void {
    setEditingId(item.id);
    setForm({
      conceptType: item.conceptType,
      name: item.name,
      fixedValue: item.fixedValue === null ? "" : String(item.fixedValue),
      isBillable: item.isBillable,
      isGross: item.isGross,
      generatesDebt: item.generatesDebt,
      status: item.status,
    });
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
    resetForm();
  }

  async function onSave(): Promise<void> {
    if (!session || !form.name.trim()) return;
    if (form.conceptType === "fixed" && !form.fixedValue.trim()) {
      setError("El valor es obligatorio para conceptos fijos");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        conceptType: form.conceptType,
        name: form.name.trim(),
        fixedValue: form.conceptType === "fixed" ? Number(form.fixedValue) : null,
        isBillable: form.isBillable,
        isGross: form.isGross,
        generatesDebt: form.generatesDebt,
        status: form.status,
      } as const;

      if (editingId) {
        const updated = await updateExpenseConcept(session, editingId, payload);
        setItems((prev) => prev.map((item) => (item.id === editingId ? updated : item)));
      } else {
        const created = await createExpenseConcept(session, payload);
        setItems((prev) => [created, ...prev]);
      }
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar gasto");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!session || !window.confirm("¿Eliminar concepto de gasto?")) return;
    try {
      await deleteExpenseConcept(session, id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar gasto");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Conceptos de gasto ({items.length})</h2>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <i className="ri-add-line" />
            Nuevo gasto
          </button>
        </div>

        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin conceptos de gasto registrados.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{item.name}</div>
                    <div className="text-xs text-gray-500">
                      {item.conceptType === "fixed" ? "Fijo" : "Variable"} · {item.status === "active" ? "Activo" : "Inactivo"}
                      {item.conceptType === "fixed" ? ` · $${Number(item.fixedValue ?? 0).toLocaleString("es-CL")}` : ""}
                      {` - ${item.isBillable ? "Facturable" : "No facturable"}`}
                      {` - ${item.isGross ? "Monto bruto" : "Monto neto"}`}
                      {` - ${item.generatesDebt ? "Genera deuda" : "Cobro inmediato"}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => editItem(item)} className="text-xs px-2 py-1 border border-gray-200 rounded">
                      Editar
                    </button>
                    <button
                      onClick={() => void onDelete(item.id)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">{editingId ? "Editar gasto" : "Nuevo gasto"}</h3>
              <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-3">
              <select
                value={form.conceptType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    conceptType: e.target.value as FormState["conceptType"],
                    fixedValue: e.target.value === "fixed" ? prev.fixedValue : "",
                  }))
                }
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="fixed">Fijo</option>
                <option value="variable">Variable</option>
              </select>

              <input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre concepto"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />

              {form.conceptType === "fixed" ? (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.fixedValue}
                  onChange={(e) => setForm((prev) => ({ ...prev, fixedValue: e.target.value }))}
                  placeholder="Valor"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              ) : null}

              <select
                value={form.isBillable ? "billable" : "non_billable"}
                onChange={(e) => setForm((prev) => ({ ...prev, isBillable: e.target.value === "billable" }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="billable">Facturable</option>
                <option value="non_billable">No facturable</option>
              </select>

              <select
                value={form.isGross ? "gross" : "net"}
                onChange={(e) => setForm((prev) => ({ ...prev, isGross: e.target.value === "gross" }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="net">Monto neto</option>
                <option value="gross">Monto bruto (IVA incluido)</option>
              </select>

              <select
                value={form.generatesDebt ? "debt" : "instant"}
                onChange={(e) => setForm((prev) => ({ ...prev, generatesDebt: e.target.value === "debt" }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="debt">Genera deuda</option>
                <option value="instant">Cobro inmediato</option>
              </select>

              <select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as FormState["status"] }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg">
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                disabled={saving || !form.name.trim() || (form.conceptType === "fixed" && !form.fixedValue.trim())}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60"
              >
                {saving ? (editingId ? "Guardando..." : "Creando...") : editingId ? "Actualizar gasto" : "Crear gasto"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
